import { Link } from 'react-router-dom'
import { useI18n } from '@/lib/i18n'
import { announcementStore } from '@/lib/variables'
import { useSectionHeading } from '@/lib/primarySection'
import type { Section } from '@/lib/sections'
import LogoMaze from '@/components/ui/LogoMaze'
import './HomeHero.css'

type Props = { onNavigate: (s: Section) => void }

type Fact = { label: string; value: string }

export default function HomeHero({ onNavigate }: Props) {
  const { section, to } = useI18n()
  const brand = section('brand') as unknown as {
    eyebrow: string
    title: string
    subtitle: string
    cta: string
    ctaSecondary: string
    facts: Fact[]
    tape?: { label: string; items: string[]; cta: string; aria: string }
  }
  const facts = Array.isArray(brand.facts) ? brand.facts : []

  const { Tag: Title, className: titleClass } = useSectionHeading('home', 'h1')

  const tape = brand.tape && Array.isArray(brand.tape.items) ? brand.tape : null
  const tapeText = tape ? [`${tape.label} · ${tape.items.join(' · ')}`, tape.cta].join(' · ') : ''

  return (
    <div className="hero">
      {/* the running tape under the header: the same band as the announcement's ticker, one link */}
      {tape && (
        <Link
          to={to('/nisg-2026')}
          className="hero__tape"
          aria-label={tape.aria}
          data-no-transition
          onClick={(e) => {
            // a plain click opens the announcement; modifier clicks keep the link
            if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey || e.button !== 0) return
            e.preventDefault()
            announcementStore.open()
          }}
        >
          <span className="hero__tape-track" aria-hidden>
            {[0, 1].map((i) => (
              <span key={i} className="hero__tape-run">
                <span className="hero__tape-dot" />
                {tapeText}
                <span className="hero__tape-arrow">→</span>
              </span>
            ))}
          </span>
        </Link>
      )}
      <div className="hero__grid">
        <div className="hero__copy">
          <span className="eyebrow">{brand.eyebrow}</span>
          <div id="hero-sentinel" style={{ height: 1 }} aria-hidden />

          <Title className={titleClass}>{brand.title}</Title>
          <p className="lede">{brand.subtitle}</p>
          <div className="hero__actions">
            <button type="button" className="btn btn--primary" onClick={() => onNavigate('contact')}>
              {brand.cta}
              <span className="arrow" aria-hidden>
                →
              </span>
            </button>
            <button type="button" className="btn" onClick={() => onNavigate('projects')}>
              {brand.ctaSecondary}
            </button>
          </div>
        </div>

        <figure className="hero__art" aria-hidden>
          <LogoMaze seed={1150} />
        </figure>
      </div>

      {facts.length > 0 && (
        <dl className="hero__facts">
          {facts.map((f) => (
            <div key={f.label} className="hero__fact">
              <dt>{f.label}</dt>
              <dd>{f.value}</dd>
            </div>
          ))}
        </dl>
      )}
    </div>
  )
}
